import { Suspense, useEffect, useMemo, useRef, useState } from 'react'
import { Canvas, useFrame } from '@react-three/fiber'
import ReactDOM from 'react-dom'

import {
  useGLTF,
  useScroll,
  ScrollControls,
  Environment,
  Merged,
  Text,
  MeshReflectorMaterial,
  Stats,
  Grid,
  MeshWobbleMaterial,
  MeshDistortMaterial,
  OrbitControls,
  RoundedBox,
  Text3D,
  Html
} from '@react-three/drei'
import cabinsData from './../averaged_people_count.json'

const colorMap = (count) => {
  if (count <= 5) return 'green'
  if (count <= 10) return '#C58F03'
  return '#B53030'
}

function Train({ cabinColors, frameIndex }) {
  const ref = useRef()
  const [cabin, seat] = useGLTF(['/cabin-transformed.glb', '/seat-transformed.glb'])
  const meshes = useMemo(() => ({ Cabin: cabin.nodes.cabin_1, Seat: seat.nodes.seat }), [cabin, seat])
  const scroll = useScroll()

  useFrame(() => {
    // Update the group's z-index relative to scroll
    ref.current.position.z = scroll.offset * 120
  })

  return (
    <Merged castShadow receiveShadow meshes={meshes}>
      {(models) => (
        <group ref={ref} position={[0, 0, 0]}>
          <Cabin
            coachNumber="1"
            models={models}
            color={cabinColors[0]}
            seatColor="sandybrown"
            countOfPeople="10"
            position={[0, 0, -6]}
            material={<MeshWobbleMaterial attach="material" color="green" speed={1} factor={0.6} />}
          />
          <Cabin
            coachNumber="2"
            models={models}
            // color="#B53030"
            color={cabinColors[1]}
            seatColor="sandybrown"
            countOfPeople="5"
            position={[0, 0, -32]}
            material={<MeshDistortMaterial attach="material" color="red" distort={0.4} speed={2} />}
          />
          <Cabin
            coachNumber="3"
            models={models}
            // color="#C58F03"
            color={cabinColors[2]}
            seatColor="sandybrown"
            countOfPeople="7"
            position={[0, 0, -58]}
            material={<MeshWobbleMaterial attach="material" color="yellow" speed={1} factor={0.6} />}
          />
          <Cabin
            coachNumber="4"
            models={models}
            // color="green"
            color={cabinColors[3]}
            seatColor="sandybrown"
            countOfPeople="2"
            position={[0, 0, -84]}
            material={<MeshDistortMaterial attach="material" color="green" distort={0.4} speed={2} />}
          />
          <Cabin
            coachNumber="5"
            models={models}
            // color="#B53030"
            color={cabinColors[4]}
            seatColor="sandybrown"
            countOfPeople="15"
            position={[0, 0, -110]}
            material={<MeshWobbleMaterial attach="material" color="red" speed={1} factor={0.6} />}
          />
          <Cabin
            coachNumber="6"
            models={models}
            // color="green"
            color={cabinColors[5]}
            seatColor="sandybrown"
            countOfPeople="4"
            position={[0, 0, -136]}
            material={<MeshDistortMaterial attach="material" color="green" distort={0.4} speed={2} />}
          />
        </group>
      )}
    </Merged>
  )
}

{
  /* Text label */
}
const Cabin = ({ models, color = 'green', seatColor = 'white', countOfPeople, material, coachNumber, ...props }) => (
  <group {...props}>
    {/* Text label */}
    {/* <Text3D fontSize={4} color="white" position={[0, 6, 4]}>
      {countOfPeople}
    </Text3D> */}
    <Text3D
      font={'/Inter_Bold.json'}
      curveSegments={32}
      bevelEnabled
      bevelSize={0.04}
      bevelThickness={0.1}
      height={0.5}
      lineHeight={0.5}
      letterSpacing={-0.06}
      size={1.5}
      rotation-y={-Math.PI / 2}
      position={[0, 3.4, -3]}>
      {/* C{coachNumber}: {countOfPeople} */}C{coachNumber}
      <meshStandardMaterial color={color} />
    </Text3D>

    {/* Cabin body */}
    <models.Cabin color={color}>{material}</models.Cabin>
    {/* Rounded box beneath the cabin */}
    <RoundedBox scale={[5, 4, 0.4]} position={[0, 2.2, 0]} rotation={[-Math.PI / 2, 0, -Math.PI / 2]} radius={0.05} smoothness={4}>
      <meshStandardMaterial color={color} />
    </RoundedBox>
    <RoundedBox scale={[20, 4, 0.4]} position={[0, 1.7, 0]} rotation={[-Math.PI / 2, 0, -Math.PI / 2]} radius={0.05} smoothness={4}>
      <meshStandardMaterial color={color} />
    </RoundedBox>
    {/* Rows of seats */}
    <Row models={models} color={seatColor} />
    <Row models={models} color={seatColor} position={[0, 0, -1.9]} />
    <Row models={models} color={seatColor} position={[0, 0, -6.6]} />
    <Row models={models} color={seatColor} position={[0, 0, -8.5]} />
    <Row models={models} color={seatColor} position={[0, 0, -11]} />
    <Row models={models} color={seatColor} position={[0, 0, -12.9]} />
    <Row models={models} color={seatColor} position={[0, 0, -17.6]} />
    <Row models={models} color={seatColor} position={[0, 0, -19.5]} />
  </group>
)

const Row = ({ models, color, ...props }) => (
  <group {...props}>
    <Quarter models={models} color={color} position={[-1.2, -0.45, 9.75]} />
    <Quarter models={models} color={color} position={[1.2, -0.45, 9.75]} />
  </group>
)

const Quarter = ({ models, color, ...props }) => (
  <group {...props}>
    <models.Seat color={color} position={[-0.35, 0, 0.7]} />
    <models.Seat color={color} position={[0.35, 0, 0.7]} />
    <models.Seat color={color} position={[-0.35, 0, -0.7]} rotation={[0, Math.PI, 0]} />
    <models.Seat color={color} position={[0.35, 0, -0.7]} rotation={[0, Math.PI, 0]} />
  </group>
)

const InfoPortal = ({ cabinColors, frameIndex, cabinsData }) => {
  const freeCoaches = []
  const moderateCoaches = []
  const fullCoaches = []

  useMemo(() => {
    cabinColors.forEach((_, index) => {
      const count = cabinsData[frameIndex][`coach ${index + 1} count`]
      if (count <= 5) {
        freeCoaches.push(`C${index + 1}`)
      } else if (count <= 10) {
        moderateCoaches.push(`C${index + 1}`)
      } else {
        fullCoaches.push(`C${index + 1}`)
      }
    })
  }, [cabinColors, frameIndex])

  return ReactDOM.createPortal(
    <div style={{ position: 'fixed', top: '10px', right: '10px', backgroundColor: 'white', padding: '10px', borderRadius: '5px', width: '200px' }}>
      <div style={{ color: 'green' }}>Free Coaches: {freeCoaches.join(', ')}</div>
      <div style={{ color: 'orange' }}>Moderate Coaches: {moderateCoaches.join(', ')}</div>
      <div style={{ color: 'red' }}>Full Coaches: {fullCoaches.join(', ')}</div>
    </div>,
    document.body
  )
}

export default function Metro() {
  const isSmallDevice = window.innerWidth < 768
  const cameraFov = isSmallDevice ? 45 : 35
  const cameraPosition = isSmallDevice ? [-40, 10, 30] : [-22, 8, 16]

  const [frameIndex, setFrameIndex] = useState(0)
  const [cabinColors, setCabinColors] = useState([])

  useEffect(() => {
    const interval = setInterval(() => {
      setFrameIndex((prevIndex) => (prevIndex + 1) % Object.keys(cabinsData).length)
      console.log(cabinsData['coach 1 count'])
    }, 5000)

    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    const frameData = cabinsData[frameIndex]
    const newColors = [
      colorMap(frameData['coach 1 count']),
      colorMap(frameData['coach 2 count']),
      colorMap(frameData['coach 3 count']),
      colorMap(frameData['coach 4 count']),
      colorMap(frameData['coach 5 count']),
      colorMap(frameData['coach 6 count'])
    ]
    setCabinColors(newColors)
  }, [frameIndex])

  return (
    <>
      <Canvas dpr={[1, 1.5]} shadows camera={{ position: cameraPosition, fov: cameraFov + 4 }} gl={{ alpha: false }}>
        <color attach="background" args={['#8A8A8B']} />
        <ambientLight intensity={0.8} />
        <directionalLight intensity={2} position={[-2, 6, 1]} />
        {/* <Sky sunPosition={[100, 20, 100]} turbidity={10} rayleigh={2} mieCoefficient={0.005} mieDirectionalG={0.8} /> */}
        <Suspense fallback={null}>
          <ScrollControls pages={3} damping={1}>
            <Train cabinColors={cabinColors} frameIndex={frameIndex} />
          </ScrollControls>
          <mesh scale={10} position={[0, -1.5, 0]} rotation={[-Math.PI / 2, 0, 0]}>
            <planeGeometry args={[50, 50]} />
            <MeshReflectorMaterial
              blur={[400, 100]}
              resolution={1024}
              mixBlur={1}
              mixStrength={15}
              depthScale={1}
              minDepthThreshold={0.85}
              color="#151515"
              metalness={0.6}
              roughness={1}
            />
          </mesh>
          <Grid infiniteGrid args={[50, 50]} position={[0, -1.4, 0]} cellSize={1} cellThickness={1} sectionSize={8} />
          <Environment preset="city" />
          {/* <Environment  files="/metro_noord_1k.hdr" background /> */}
        </Suspense>{' '}
        {/* <OrbitControls /> */}
        <Stats />
      </Canvas>
      <InfoPortal cabinColors={cabinColors} frameIndex={frameIndex} cabinsData={cabinsData} />
    </>
  )
}

/**

burner.pro


 */
